import { Article } from '../articlesData';

export const articleOkKnapTouchEid: Article = {
  id: 'ok-knap-touch-eller-eid-digital-signatur',
  slug: 'ok-knap-touch-eller-eid-digital-signatur',
  title: 'OK-knap, touch-underskrift eller eID: Hvad er den juridiske forskel?',
  subtitle: 'Fra simple klik til stærk to-faktor eID: Forstå forskellen på simple elektroniske signaturer (SES) og eID som MitID i forhold til bevisbyrde og retsgyldighed.',
  metaTitle: 'OK-knap, Touch eller eID: Juridisk Forskel | KvikSign',
  metaDescription: 'Hvad er forskellen på et klik på en OK-knap, en tegnet touch-signatur og MitID eID? Få styr på bevisbyrde, eIDAS-niveauer og domstolenes vurdering.',
  canonicalUrl: 'https://kviksign.com/da/articles/ok-knap-touch-eller-eid-digital-signatur',
  heroImage: '/images/articles/ok-knap-touch-eller-eid-digital-signatur.jpg',
  heroImageAlt: 'Biometrisk godkendelse med fingeraftryk og Touch ID ved digital signering',
  category: 'Juridisk',
  author: {
    name: 'KvikSign',
    role: 'eID & Sikkerhedsteam',
    avatar: '/icon.svg'
  },
  publishedAt: '2026-04-25',
  updatedAt: '2026-04-25',
  readingTime: '8 min læsetid',
  keywords: [
    'touch underskrift',
    'OK-knap signatur',
    'SES vs AES',
    'MitID underskrift',
    'bevisbyrde digital signatur',
    'eIDAS forordning',
    'retsgyldighed e-signatur',
    'KvikSign',
    'digital aftale'
  ],
  keyTakeaways: [
    'En simpel elektronisk signatur (SES) såsom et klik eller en tegnet krusedulle på en touch-skærm er juridisk bindende, men har en svag bevisværdi, hvis modparten benægter at have skrevet under.',
    'Ved touch- eller klik-underskrifter påhviler den fulde bevisbyrde dig som afsender: Du skal kunne bevise IP-adresse, e-mail-adgang og tidsforløb i retten.',
    'Stærk eID (MitID / BankID) udgør en avanceret elektronisk signatur (AES), hvor bevisbyrden i praksis vendes, fordi signaturen er kryptografisk bundet til en verificeret 2-faktor identitet.',
    'Kombinationen af et sikkert PDF-format (PAdES), SHA-256 hash og en uafhængig tidsstempling (TSA) sikrer, at dokumentets indhold ikke kan ændres efter signering.',
    'KvikSign anbefaler stærk eID til alle væsentlige aftaler og kontrakter, hvor økonomisk eller juridisk risiko gør beviskraften afgørende.'
  ],
  tableOfContents: [
    { id: 'de-tre-signaturmetoder-i-hverdagen', title: 'De tre signaturmetoder i hverdagen' },
    { id: 'aftalelovens-formfrihed-og-dens-graenser', title: 'Aftalelovens formfrihed og dens grænser' },
    { id: 'bevisbyrden-i-retten-hvem-skal-bevise-hvad', title: 'Bevisbyrden i retten: Hvem skal bevise hvad?' },
    { id: 'teknisk-sammenligning-af-sikkerhedsniveauer', title: 'Teknisk sammenligning: Fra pixel-billede til kryptografisk hash' },
    { id: 'risikomatrix-hvilken-metode-til-hvilket-dokument', title: 'Risikomatrix: Vælg den rigtige signeringsmetode' },
    { id: 'konklusion-hvorfor-eid-er-fremtiden', title: 'Konklusion: Hvorfor stærk eID bør være din virksomheds standard' },
    { id: 'kilder', title: 'Kilder og juridisk reference' }
  ],
  sections: [
    {
      id: 'de-tre-signaturmetoder-i-hverdagen',
      title: 'De tre signaturmetoder i hverdagen',
      paragraphs: [
        'I den digitale verden møder vi dagligt vidt forskellige måder at "skrive under" på. Når vi handler på nettet, klikker vi på en "Accepter betingelser"-knap. Når vi modtager en pakke fra et fragtfirma, tegner vi en krusedulle med fingeren på en håndholdt terminal. Og når vi underskriver en ansættelseskontrakt eller en bankaftale, logger vi ind med vores personlige MitID.',
        'Selvom alle tre handlinger i daglig tale kaldes for digitale underskrifter, er der en verden til forskel på deres tekniske opbygning, sikkerhed og retsvirkning, hvis parterne efterfølgende ender i en juridisk tvist ved domstolene.',
        'eIDAS-forordningen skelner mellem simple, avancerede og kvalificerede signaturer. At forstå, hvorfor en tegnet underskrift på en skærm blot er en simpel signatur (SES), er afgørende for at beskytte virksomhedens værdier og kontrakter mod indsigelser.'
      ]
    },
    {
      id: 'aftalelovens-formfrihed-og-dens-graenser',
      title: 'Aftalelovens formfrihed og dens grænser',
      paragraphs: [
        'Dansk aftaleret bygger på det grundlæggende princip om formfrihed, der stammer helt tilbage fra Danske Lov af 1683 og er stadfæstet i Aftalelovens § 1. Det betyder, at en aftale som udgangspunkt er lige så juridisk bindende, uanset om den er indgået mundtligt, ved et håndtryk, via en SMS, ved et klik på en hjemmeside eller på et stykke papir med blæk.',
        'Hertil kommer eIDAS-forordningens artikel 25, stk. 1, som udtrykkeligt fastslår, at en elektronisk underskrift ikke må nægtes retsvirkning eller afvises som bevis ved en domstol, udelukkende fordi den har elektronisk form eller ikke opfylder kravene til en kvalificeret signatur.',
        'Men selvom en aftale i teorien er bindende ved et simpelt klik, er der et gigantisk gab mellem materiel gyldighed og processuel beviskraft. Hvis modparten hævder: "Det var ikke mig, der klikkede på den knap", er det formfriheden, der kollapser mod virkelighedens beviskrav.'
      ]
    },
    {
      id: 'bevisbyrden-i-retten-hvem-skal-bevise-hvad',
      title: 'Bevisbyrden i retten: Hvem skal bevise hvad?',
      paragraphs: [
        'Hvis en debitor nægter at have underskrevet en gældsforpligtelse eller en kontrakt, følger bevisbyrden efter dansk retspleje det klare udgangspunkt: Den, der påstår en rettighed eller en fordring, bærer bevisbyrden for, at aftalen er gyldigt indgået af modparten.',
        'Ved brug af OK-knap eller touch-underskrift er bevisbyrden ekstremt tung at løfte for kreditor. En tegnet krusedulle på en skærm indeholder ingen biometriske data (såsom pennetryk, vinkel eller hastighed), og et billede af en underskrift kan let kopieres fra ét dokument til et andet. IP-adresser og e-mail-bekræftelser kan sandsynliggøre, at nogen på en given adresse modtog linket, men det beviser ikke, hvilken person i husstanden eller på kontoret der trykkede.',
        'Ved brug af stærk eID som MitID er situationen en ganske anden. MitID er baseret på to-faktor-autentifikation og en personlig hemmelighed. Domstolene lægger til grund, at når en handling er godkendt med MitID, formodes handlingen at være foretaget af certifikatindehaveren, medmindre indehaveren kan godtgøre, at MitID har været udsat for misbrug under omstændigheder, der ikke kan lægges vedkommende til last.'
      ]
    },
    {
      id: 'teknisk-sammenligning-af-sikkerhedsniveauer',
      title: 'Teknisk sammenligning: Fra pixel-billede til kryptografisk hash',
      paragraphs: [
        'Forskellene bliver endnu tydeligere, når man ser på den tekniske implementering bag de tre metoder:',
        '1. OK-knap / Checkbox: Genererer blot en logpost i en database. Hvis databasen slettes eller manipuleres, er beviset borte. Dokumentet og accepten er ikke uløseligt bundet sammen.',
        '2. Touch-underskrift (PNG/SVG overlejring): Et rent visuelt billede placeres oven på en PDF. Hvis PDF-filen ikke er kryptografisk forseglet, kan teksten i PDF-filen ændres efterfølgende, uden at underskriftsbilledet forsvinder.',
        '3. eID med PAdES-forsegling (KvikSign-metoden): Dokumentets indhold beregnes til et matematisk fingeraftryk (SHA-256 hash). Ved signering med MitID krypteres dette fingeraftryk med et OCES3-certifikat og forsegles med et officielt tidsstempel. Ændres blot ét komma i dokumentet efter underskrift, vil signaturen øjeblikkeligt fremstå som brudt og ugyldig.'
      ]
    },
    {
      id: 'risikomatrix-hvilken-metode-til-hvilket-dokument',
      title: 'Risikomatrix: Vælg den rigtige signeringsmetode',
      paragraphs: [
        'Virksomheder bør have en klar politik for, hvilke aftaler der må underskrives med simple metoder, og hvilke der kræver stærk eID:',
        'Lav risiko (SES acceptabel): Modtagelse af pakker, interne frokostordninger, nyhedsbrevstilmeldinger, simple samtykker uden økonomisk forpligtelse.',
        'Mellem- og høj risiko (MitID / eID påkrævet): Ansættelseskontrakter, fratrædelsesaftaler, B2B-leverandørkontrakter, lånedokumenter, fortrolighedsaftaler (NDA), overdragelse af immaterielle rettigheder, leasingaftaler og bestyrelsesreferater.'
      ],
      listItems: [
        'Lav risiko: Simpel klik-accept kan være acceptabel af hensyn til minimal friktion.',
        'Høj risiko: Kræv altid MitID eller MitID Erhverv for at sikre uigendrivelig beviskraft.',
        'Grænseoverskridende: Kræv eIDAS-kompatibel eID eller QES for aftaler med udenlandske modparter.'
      ]
    },
    {
      id: 'konklusion-hvorfor-eid-er-fremtiden',
      title: 'Konklusion: Hvorfor stærk eID bør være din virksomheds standard',
      paragraphs: [
        'I en tid med stigende digital svindel, phishing og identitetstyveri er simple touch-underskrifter og usikre klik-løsninger en unødvendig risiko for professionelle virksomheder. Det tager ikke længere tid for en kunde eller medarbejder at swipe i MitID-appen end at tegne en ulæselig streg på en skærm, men forskellen i juridisk tryghed er enorm.',
        'Med KvikSign får du en fuldautomatiseret platform, der integrerer MitID direkte i ethvert aftaleflow, sikrer automatisk PAdES-forsegling og genererer en uafhængig, revisionsklar auditlog.'
      ]
    }
  ],
  faqs: [
    {
      question: 'Er en underskrift med fingeren på en iPad juridisk gyldig i Danmark?',
      answer: 'Ja, den er i princippet gyldig efter Aftalelovens formfrihed, men den betragtes som en simpel elektronisk signatur (SES). Hvis underskriveren benægter at have skrevet under, er det meget vanskeligt at bevise ægtheden i retten.'
    },
    {
      question: 'Hvorfor er MitID stærkere end en indscannet underskrift?',
      answer: 'MitID er baseret på to-faktor-autentifikation og krypteres direkte ind i dokumentets certifikat. En indscannet underskrift er blot et billede, som enhver kan kopiere og indsætte på et dokument uden ejerens viden.'
    },
    {
      question: 'Hvad er en PAdES-forsegling?',
      answer: 'PAdES (PDF Advanced Electronic Signatures) er en international teknisk standard (ETSI), der integrerer kryptografiske signaturer, certifikater og tidsstempler direkte i selve PDF-filen, så manipulation straks afsløres.'
    },
    {
      question: 'Hvad er en uafhængig tidsstempling (TSA)?',
      answer: 'En Time Stamping Authority (TSA) er en betroet tredjepart, der udsteder et kryptografisk bevis for, at dokumentet eksisterede i præcis denne form på et bestemt sekund, uafhængigt af computerens lokale ur.'
    },
    {
      question: 'Kan man kombinere eID-signering med KvikSend fildeling?',
      answer: 'Ja! KvikSend giver mulighed for at beskytte selve dokumentet med MitID-adgangskontrol, før modtageren overhovedet kan åbne eller signere det, hvilket opfylder Datatilsynets strengeste krav.'
    }
  ],
  sources: [
    {
      title: 'eIDAS-forordningen (Europa-Parlamentets og Rådets forordning EU 910/2014)',
      url: 'https://eur-lex.europa.eu/legal-content/DA/TXT/?uri=CELEX:32014R0910'
    },
    {
      title: 'Aftaleloven (Lov om aftaler og andre retshandler på formuerettens område)',
      url: 'https://www.retsinformation.dk/eli/lta/2016/193'
    },
    {
      title: 'Digitaliseringsstyrelsen - Sikkerhedsniveauer for eID',
      url: 'https://digst.dk/it-loesninger/mitid/'
    },
    {
      title: 'Domstolsstyrelsen - Bevisførelse og elektroniske dokumenter',
      url: 'https://domstol.dk/'
    }
  ],
  disclaimer: 'Artiklen giver et overblik over gældende ret og teknisk praksis. Den kan ikke erstatte konkret juridisk sagsbehandling ved en domstol eller advokatbistand.'
};
